import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { apiGetCycles, apiCreateCycle, apiUpdateCycle } from '../services/api';
import InfoButton from '../components/InfoButton';
import { useFormDate } from '../utils/formDate';
import './FormCycle.css';

/* ===== Form icons / emojis ===== */
import spotting from '../assets/forms/form_emojis/spotting.png';
import faible from '../assets/forms/form_emojis/faible.png';
import moyen from '../assets/forms/form_emojis/moyen.png';
import abondant from '../assets/forms/form_emojis/abondant.png';
import absence from '../assets/forms/form_emojis/absence.png';
import blancOeuf from '../assets/forms/form_emojis/blanc oeuf.png';
import visqueuse from '../assets/forms/form_emojis/visqueuse.png';
import cremeuse from '../assets/forms/form_emojis/cremeuse.png';
import grumeleuse from '../assets/forms/form_emojis/grumeleuse.png';
import spotting2 from '../assets/forms/form_emojis/spotting2.png';
import grande from '../assets/forms/form_emojis/grande.png';
import habituelle from '../assets/forms/form_emojis/habituelle.png';
import petite from '../assets/forms/form_emojis/petite.png';
import fatigue from '../assets/forms/form_emojis/fatigue.png';
import ballonnements from '../assets/forms/form_emojis/ballonnements.png';
import constipation from '../assets/forms/form_emojis/constipation.png';
import jambesLourdes from '../assets/forms/form_emojis/jambes lourdes.png';
import mauxDeTete from '../assets/forms/form_emojis/maux de tete.png';
import gonflement from '../assets/forms/form_emojis/gonflement.png';
import crampes from '../assets/forms/form_emojis/crampes.png';
import acne from '../assets/forms/form_emojis/acne.png';
import courbatures from '../assets/forms/form_emojis/courbatures.png';
import diffDormir from '../assets/forms/form_emojis/difficulte dormir.png';
import reveilsFreq from '../assets/forms/form_emojis/reveils frequents.png';
import energie from '../assets/forms/form_emojis/energie.png';
import normale from '../assets/forms/form_emojis/normale.png';

import { ReactComponent as DropletP } from '../assets/forms/form_emojis/dropletp.svg';
import { ReactComponent as DropletW } from '../assets/forms/form_emojis/dropletw.svg';
import { ReactComponent as HeartP } from '../assets/forms/form_emojis/heartp.svg';
import { ReactComponent as HeartG } from '../assets/forms/form_emojis/heartg.svg';
import { ReactComponent as HeartbeatP } from '../assets/forms/form_emojis/heartbeatp.svg';
import { ReactComponent as HeartbeatG } from '../assets/forms/form_emojis/heartbeatg.svg';
import { ReactComponent as Coffee } from '../assets/forms/form_emojis/coffeep.svg';
import { ReactComponent as Croissant } from '../assets/forms/form_emojis/croissantp.svg';
import { ReactComponent as Smiley } from '../assets/forms/form_emojis/smileyp.svg';
import { ReactComponent as Arrow } from '../assets/forms/form_emojis/arrowp.svg';
import { ReactComponent as Energy } from '../assets/forms/form_emojis/energyp.svg';

const FLUX_MAP = { spotting: 'Spotting', faible: 'Faible', moyen: 'Moyen', abondant: 'Abondant' };
const PERTES_MAP = {
  absence: 'Absence', 'blanc-oeuf': "Blanc d'œuf", visqueuse: 'Visqueuses',
  cremeuse: 'Crèmeuse', grumeleuse: 'Grumeleuses', spotting2: 'Spotting',
};
const LIBIDO_MAP = { grande: 'Grande', habituelle: 'Habituelle', petite: 'Petite' };
const SYMPTOMES_MAP = {
  fatigue: 'Fatigue', ballonnements: 'Ballonnements', constipation: 'Constipation',
  'jambes-lourdes': 'Jambes lourdes', 'maux-tete': 'Maux de tête',
  gonflement: 'Gonflement poitrine', crampes: 'Crampes', acne: 'Acné',
  courbatures: 'Courbatures', 'selles-molles': 'Selles molles',
  'douleurs-ovariennes': 'Douleurs ovariennes', 'mal-dos': 'Mal au dos',
};

// Précision demandée dès que « Mal au dos » est sélectionné. Champ à part plutôt que
// deux tags distincts dans `physical_symptoms` : le compteur de symptômes reste sur un
// seul libellé, et l'origine se lit indépendamment de la fréquence.
const BACK_PAIN_MAP = { hormonal: 'Hormonal', mecanique: 'Mécanique' };
const BACK_PAIN_REV = Object.fromEntries(Object.entries(BACK_PAIN_MAP).map(([k, v]) => [v, k]));

const CRAVING_TYPES = ['Sucré', 'Salé', 'Chocolat', 'Gras', 'Épicé', 'Fast food'];
const SOMMEIL_MAP = {
  'bien-dormi': 'Bien dormi', 'reveils-freq': 'Réveils fréquents',
  'sommeil-interrompu': 'Sommeil interrompu',
  'diff-dormir': "Difficulté à s'endormir",
};
const ETAT_REVEIL_MAP = { fatiguee: 'Fatiguée', normale: 'Normale', 'pleine-energie': "Pleine d'énergie" };

// Reverse maps for pre-population
const FLUX_REV = Object.fromEntries(Object.entries(FLUX_MAP).map(([k, v]) => [v, k]));
const PERTES_REV = Object.fromEntries(Object.entries(PERTES_MAP).map(([k, v]) => [v, k]));
const LIBIDO_REV = Object.fromEntries(Object.entries(LIBIDO_MAP).map(([k, v]) => [v, k]));
const SYMPTOMES_REV = Object.fromEntries(Object.entries(SYMPTOMES_MAP).map(([k, v]) => [v, k]));
const SOMMEIL_REV = Object.fromEntries(Object.entries(SOMMEIL_MAP).map(([k, v]) => [v, k]));
const ETAT_REVEIL_REV = Object.fromEntries(Object.entries(ETAT_REVEIL_MAP).map(([k, v]) => [v, k]));

const FormCycle = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, getToken } = useAuth();
  const [error, setError] = useState('');
  const [existingId, setExistingId] = useState(null);
  const [isDirty, setIsDirty] = useState(false);
  const isLoading = useRef(true);

  // Jour édité, et propagation de la date aux autres onglets : passer sur Mental ou
  // Sport en plein rattrapage ramenait silencieusement la saisie à aujourd'hui.
  const { selectedDate, isBackfill, label: dateLabel, withDate } = useFormDate();

  /* ===== State pour chaque section ===== */
  const [jourRepos, setJourRepos] = useState(false);
  const [flux, setFlux] = useState(null);
  const [pertes, setPertes] = useState(null);
  // Deux façons de renseigner la température basale, exclusives. La variation est
  // le mode par défaut : relever 36,71 °C demande un thermomètre précis et une
  // lecture soignée, alors que le signal recherché — le plateau lutéal de +0,3 à
  // +0,5 °C — est un écart, pas une valeur. Le backend accepte les deux clés et
  // les ramène à une même série (core/cycle_engine.bbt_series).
  const [tempMode, setTempMode] = useState('variation'); // 'variation' | 'exacte'
  const [temperature, setTemperature] = useState('');
  const [tempDelta, setTempDelta] = useState(null);
  // Une nuit blanche, de la fièvre ou de l'alcool décalent la température au réveil
  // de plusieurs dixièmes : le relevé est conservé mais exclu du plateau thermique,
  // sans quoi il déclencherait une fausse confirmation d'ovulation.
  const [tempDisturbed, setTempDisturbed] = useState(false);
  const [libido, setLibido] = useState(null);
  const [fringales, setFringales] = useState(false);
  const [cravingTypes, setCravingTypes] = useState([]);
  const [testOvulation, setTestOvulation] = useState(null);
  // Déclaration directe, distincte du test urinaire : elle affirme que *ce jour* est le
  // jour d'ovulation. Le moteur la place juste sous la confirmation thermique, au-dessus
  // de la glaire fertile (core/cycle_engine.observed_ovulation_date).
  const [ovulationDeclared, setOvulationDeclared] = useState(false);
  const [symptomes, setSymptomes] = useState([]);
  const [backPainType, setBackPainType] = useState(null);
  const [sommeil, setSommeil] = useState(null);
  const [etatReveil, setEtatReveil] = useState(null);
  const [note, setNote] = useState('');

  const markDirty = () => { if (!isLoading.current) setIsDirty(true); };

  // Pre-populate when selectedDate changes
  useEffect(() => {
    if (!user?.id) return;
    isLoading.current = true;
    setIsDirty(false);
    setExistingId(null);
    setJourRepos(false); setFlux(null); setPertes(null);
    setTemperature(''); setTempDelta(null); setTempMode('variation'); setTempDisturbed(false);
    setLibido(null); setFringales(false); setCravingTypes([]); setTestOvulation(null);
    setOvulationDeclared(false);
    setSymptomes([]); setBackPainType(null); setSommeil(null); setEtatReveil(null); setNote('');

    apiGetCycles(user.id, getToken()).then((entries) => {
      const entry = entries.find((e) => e.date === selectedDate);
      if (entry) {
        setExistingId(entry.id);
        setJourRepos(entry.is_rest_day ?? false);
        setFlux(entry.flow ? (FLUX_REV[entry.flow] ?? null) : null);
        setPertes(entry.discharge ? (PERTES_REV[entry.discharge] ?? null) : null);
        // Le relevé rouvre dans le mode où il a été saisi, sinon une valeur
        // absolue enregistrée hier réapparaîtrait comme un écart vide.
        setTemperature(entry.basal_temperature != null ? String(entry.basal_temperature) : '');
        setTempDelta(entry.basal_temperature_delta ?? null);
        setTempMode(entry.basal_temperature != null ? 'exacte' : 'variation');
        setTempDisturbed(entry.temperature_disturbed ?? false);
        setLibido(entry.libido ? (LIBIDO_REV[entry.libido] ?? null) : null);
        setFringales(entry.cravings ?? false);
        setCravingTypes(entry.craving_types ?? []);
        setTestOvulation(entry.ovulation_test ?? null);
        setOvulationDeclared(entry.ovulation_declared ?? false);
        setSymptomes((entry.physical_symptoms ?? []).map((s) => SYMPTOMES_REV[s]).filter(Boolean));
        setBackPainType(entry.back_pain_type ? (BACK_PAIN_REV[entry.back_pain_type] ?? null) : null);
        setSommeil(entry.sleep_quality ? (SOMMEIL_REV[entry.sleep_quality] ?? null) : null);
        setEtatReveil(entry.wake_up_state ? (ETAT_REVEIL_REV[entry.wake_up_state] ?? null) : null);
        setNote(entry.notes ?? '');
      }
    }).catch(() => {}).finally(() => { isLoading.current = false; });
  }, [user?.id, getToken, selectedDate]);

  const toggleSymptome = (s) => {
    setSymptomes((prev) =>
      prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]
    );
    // Retirer « Mal au dos » doit emporter sa précision, sans quoi le serveur
    // recevrait « pas de mal de dos, mais hormonal ».
    if (s === 'mal-dos' && symptomes.includes(s)) setBackPainType(null);
    markDirty();
  };

  // La virgule est la séparation décimale naturelle sur un clavier français, et
  // `parseFloat('36,7')` renvoie 36 — soit une température fausse de 0,7 °C qui
  // passe la validation serveur sans que rien ne le signale.
  const parseDecimal = (raw) => {
    const value = parseFloat(String(raw).replace(',', '.'));
    return Number.isFinite(value) ? value : null;
  };

  const handleSubmit = async () => {
    setError('');
    if (!isDirty && !existingId) { setError('Aucune donnée saisie.'); return; }

    const exactValue = tempMode === 'exacte' && temperature ? parseDecimal(temperature) : null;
    if (tempMode === 'exacte' && temperature && exactValue === null) {
      setError('Température invalide. Exemple attendu : 36,7');
      return;
    }

    const payload = {
      date: selectedDate,
      is_rest_day: jourRepos,
      flow: flux ? FLUX_MAP[flux] : null,
      discharge: pertes ? PERTES_MAP[pertes] : null,
      // Les deux clés sont exclusives : envoyer les deux laisserait le serveur
      // arbitrer entre une valeur et un écart qui peuvent se contredire.
      basal_temperature: exactValue,
      basal_temperature_delta: tempMode === 'variation' ? tempDelta : null,
      temperature_disturbed: tempDisturbed,
      libido: libido ? LIBIDO_MAP[libido] : null,
      cravings: fringales,
      craving_types: cravingTypes.length ? cravingTypes : null,
      ovulation_test: testOvulation,
      ovulation_declared: ovulationDeclared,
      physical_symptoms: symptomes.map((s) => SYMPTOMES_MAP[s]).filter(Boolean),
      back_pain_type: symptomes.includes('mal-dos') && backPainType
        ? BACK_PAIN_MAP[backPainType]
        : null,
      sleep_quality: sommeil ? SOMMEIL_MAP[sommeil] : null,
      wake_up_state: etatReveil ? ETAT_REVEIL_MAP[etatReveil] : null,
      notes: note || null,
    };
    try {
      let cycleResponse;
      if (existingId) {
        const { date: _, ...payloadWithoutDate } = payload;
        cycleResponse = await apiUpdateCycle(existingId, payloadWithoutDate, getToken());
      } else {
        cycleResponse = await apiCreateCycle(payload, getToken());
      }
      // Ouvert depuis « Ajouter mon cycle » : seul ce volet était à saisir.
      if (location.state?.solo) { navigate('/accueil'); return; }
      navigate(withDate('/formulaire/mental'), { state: { cycleData: cycleResponse } });
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="form-cycle">
      {/* Header */}
      <div className="form-cycle__header">
        <div className="form-cycle__header-top">
          <div className="form-cycle__header-left">
            <div className="form-cycle__header-logo">
              <DropletW className="form-cycle__header-icon" />
            </div>
            <div>
              <h1 className="form-cycle__header-title">Cycle & symptômes</h1>
              {/* En rattrapage, le jour édité doit être visible : sans ça, rien ne
                  distingue à l'écran la saisie du jour de celle d'un jour passé. */}
              <p className="form-cycle__header-sub">
                {isBackfill ? `Saisie pour ${dateLabel}` : 'Suivi quotidien de ton cycle menstruel'}
              </p>
            </div>
          </div>
          <button className="form-cycle__close" onClick={() => navigate('/accueil')} aria-label="Fermer">✕</button>
        </div>

        {/* Onglets catégories */}
        <div className="form-cycle__tabs-wrapper">
          <div className="form-cycle__tabs">
            <button className="form-cycle__tab form-cycle__tab--active">
              <DropletW className="form-cycle__tab-icon" /> Cycle
            </button>
            <button className="form-cycle__tab" onClick={() => navigate(withDate('/formulaire/mental'))}>
              <HeartG className="form-cycle__tab-icon" /> Mental
            </button>
            <button className="form-cycle__tab" onClick={() => navigate(withDate('/formulaire/sport'))}>
              <HeartbeatG className="form-cycle__tab-icon" /> Sport
            </button>
          </div>
        </div>
      </div>

      {/* ===== Jour de repos ===== */}
      <div className="form-cycle__section form-cycle__section--bordered form-cycle__section--rest">
        <div className="form-cycle__section-header">
          <Croissant className="form-cycle__section-icon croissant" />
          <div>
            <h3 className="form-cycle__section-title">Pas de données aujourd'hui</h3>
            <p className="form-cycle__section-sub">Ignorer le suivi pour cette journée</p>
          </div>
        </div>
        <label className="form-cycle__switch">
          <input type="checkbox" checked={jourRepos} onChange={() => { setJourRepos(!jourRepos); markDirty(); }} />
          <span className="form-cycle__switch-slider" />
        </label>
      </div>

      {/* ===== Flux menstruel ===== */}
      <div className={`form-cycle__section form-cycle__section--col${jourRepos ? ' form-cycle__section--hidden' : ''}`}>
        <div className="form-cycle__section-header">
          <Arrow className="form-cycle__section-icon" />
          <div>
            <h3 className="form-cycle__section-title">Flux menstruel</h3>
            <p className="form-cycle__section-sub">Quelle est l'intensité de ton flux aujourd'hui ?</p>
          </div>
        </div>
        <div className="form-cycle__options form-cycle__options--2x2">
          {[
            { id: 'aucun', img: null, label: 'Pas de flux', sub: 'Aucun saignement' },
            { id: 'spotting', img: spotting, label: 'Spotting', sub: 'Traces légères' },
            { id: 'faible', img: faible, label: 'Faible', sub: 'Flux léger' },
            { id: 'moyen', img: moyen, label: 'Moyen', sub: 'Flux normal' },
            { id: 'abondant', img: abondant, label: 'Abondant', sub: 'Flux important' },
          ].map((o) => (
            <button
              key={o.id}
              className={`form-cycle__option form-cycle__option--large${flux === o.id ? ' form-cycle__option--selected' : ''}`}
              onClick={() => { setFlux(flux === o.id ? null : o.id); markDirty(); }}
            >
              {o.img ? <img src={o.img} alt={o.label} className="form-cycle__option-img" /> : <span style={{ fontSize: '1.6rem', margin: '4px 0' }}>🚫</span>}
              <span className="form-cycle__option-label">{o.label}</span>
              <span className="form-cycle__option-sub">{o.sub}</span>
            </button>
          ))}
        </div>
      </div>

      {/* ===== Pertes vaginales ===== */}
      <div className={`form-cycle__section form-cycle__section--col${jourRepos ? ' form-cycle__section--hidden' : ''}`}>
        <div className="form-cycle__section-header">
          <DropletP className="form-cycle__section-icon" />
          <div>
            <h3 className="form-cycle__section-title">Pertes vaginales<InfoButton infoKey="formPertes" /></h3>
            <p className="form-cycle__section-sub">Type de pertes observées</p>
          </div>
        </div>
        <div className="form-cycle__options form-cycle__options--3col">
          {[
            { id: 'absence', img: absence, label: 'Absence' },
            { id: 'blanc-oeuf', img: blancOeuf, label: "Blanc d'œuf" },
            { id: 'visqueuse', img: visqueuse, label: 'Visqueuses' },
            { id: 'cremeuse', img: cremeuse, label: 'Crémeuse' },
            { id: 'grumeleuse', img: grumeleuse, label: 'Grumeleuses' },
            { id: 'spotting2', img: spotting2, label: 'Spotting' },
          ].map((o) => (
            <button
              key={o.id}
              className={`form-cycle__option${pertes === o.id ? ' form-cycle__option--selected' : ''}`}
              onClick={() => { setPertes(pertes === o.id ? null : o.id); markDirty(); }}
            >
              <img src={o.img} alt={o.label} className="form-cycle__option-img" />
              <span className="form-cycle__option-label">{o.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* ===== Température basale ===== */}
      <div className={`form-cycle__section form-cycle__section--col${jourRepos ? ' form-cycle__section--hidden' : ''}`}>
        <div className="form-cycle__section-header">
          <HeartbeatP className="form-cycle__section-icon" />
          <div>
            <h3 className="form-cycle__section-title">Température basale<InfoButton infoKey="formTemperature" /></h3>
            <p className="form-cycle__section-sub">
              {tempMode === 'variation'
                ? 'Par rapport à ton réveil habituel'
                : 'Température au réveil en °C'}
            </p>
          </div>
        </div>

        <div className="form-cycle__temp-modes">
          {[
            { id: 'variation', label: 'Variation' },
            { id: 'exacte', label: 'Valeur exacte' },
          ].map((m) => (
            <button
              key={m.id}
              type="button"
              className={`form-cycle__temp-mode${tempMode === m.id ? ' form-cycle__temp-mode--active' : ''}`}
              onClick={() => { setTempMode(m.id); markDirty(); }}
            >
              {m.label}
            </button>
          ))}
        </div>

        {tempMode === 'variation' ? (
          <>
            {/* Crans de 0,1 °C sur ±0,4 : le plateau lutéal se joue à +0,3/+0,5, et
                une graduation plus fine demanderait une précision de mesure que la
                saisie en variation cherche justement à éviter. */}
            <div className="form-cycle__temp-scale">
              {[-0.4, -0.3, -0.2, -0.1, 0, 0.1, 0.2, 0.3, 0.4].map((d) => (
                <button
                  key={d}
                  type="button"
                  className={`form-cycle__temp-step${tempDelta === d ? ' form-cycle__temp-step--active' : ''}`}
                  onClick={() => { setTempDelta(tempDelta === d ? null : d); markDirty(); }}
                >
                  {d === 0 ? '=' : `${d > 0 ? '+' : '−'}${Math.abs(d).toFixed(1)}`}
                </button>
              ))}
            </div>
            <p className="form-cycle__temp-hint">
              {tempDelta === null && 'Choisis l’écart ressenti au thermomètre, ou laisse vide.'}
              {tempDelta === 0 && 'Comme d’habitude.'}
              {tempDelta !== null && tempDelta > 0
                && `${tempDelta.toFixed(1)} °C au-dessus de ton habitude.`}
              {tempDelta !== null && tempDelta < 0
                && `${Math.abs(tempDelta).toFixed(1)} °C en dessous de ton habitude.`}
            </p>
          </>
        ) : (
          <input
            type="text"
            inputMode="decimal"
            className="form-cycle__input"
            placeholder="Ex: 36,71"
            aria-label="Température basale en degrés Celsius"
            value={temperature}
            onChange={(e) => { setTemperature(e.target.value); markDirty(); }}
          />
        )}

        {/* Nuit blanche, fièvre, alcool : la mesure du matin est faussée. On la
            garde en historique, mais elle ne doit pas entrer dans la détection du
            plateau thermique — sinon elle déclenche une fausse ovulation. */}
        <label className="form-cycle__temp-disturbed">
          <input
            type="checkbox"
            checked={tempDisturbed}
            onChange={(e) => { setTempDisturbed(e.target.checked); markDirty(); }}
          />
          <span>Température perturbée (nuit blanche, fièvre, alcool)</span>
        </label>
      </div>

      {/* ===== Libido ===== */}
      <div className={`form-cycle__section form-cycle__section--col${jourRepos ? ' form-cycle__section--hidden' : ''}`}>
        <div className="form-cycle__section-header">
          <HeartP className="form-cycle__section-icon" />
          <div>
            <h3 className="form-cycle__section-title">Libido</h3>
          </div>
        </div>
        <div className="form-cycle__options form-cycle__options--3col">
          {[
            { id: 'grande', img: grande, label: 'Grande', sub: 'Élevée' },
            { id: 'habituelle', img: habituelle, label: 'Habituelle', sub: 'Stable' },
            { id: 'petite', img: petite, label: 'Petite', sub: 'Basse' },
          ].map((o) => (
            <button
              key={o.id}
              className={`form-cycle__option${libido === o.id ? ' form-cycle__option--selected' : ''}`}
              onClick={() => { setLibido(libido === o.id ? null : o.id); markDirty(); }}
            >
              <img src={o.img} alt={o.label} className="form-cycle__option-img" />
              <span className="form-cycle__option-label">{o.label}</span>
              <span className="form-cycle__option-sub">{o.sub}</span>
            </button>
          ))}
        </div>
      </div>

      {/* ===== Fringales ===== */}
      <div className={`form-cycle__section form-cycle__section--col form-cycle__section--bordered${jourRepos ? ' form-cycle__section--hidden' : ''}`}>
        <div className="form-cycle__section-top">
          <div className="form-cycle__section-header">
            <Coffee className="form-cycle__section-icon" />
            <div>
              <h3 className="form-cycle__section-title">Fringales</h3>
              <p className="form-cycle__section-sub">As-tu des envies particulières ?</p>
            </div>
          </div>
          <label className="form-cycle__switch">
            <input type="checkbox" checked={fringales} onChange={() => { setFringales(!fringales); if (fringales) setCravingTypes([]); markDirty(); }} />
            <span className="form-cycle__switch-slider" />
          </label>
        </div>
        {fringales && (
          <div className="form-cycle__craving-chips">
            {CRAVING_TYPES.map(type => (
              <button
                key={type}
                className={`form-cycle__craving-chip${cravingTypes.includes(type) ? ' form-cycle__craving-chip--on' : ''}`}
                onClick={() => { setCravingTypes(prev => prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]); markDirty(); }}
              >
                {type}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* ===== Test d'ovulation ===== */}
      <div className={`form-cycle__section form-cycle__section--col${jourRepos ? ' form-cycle__section--hidden' : ''}`}>
        <div className="form-cycle__section-header">
          <Energy className="form-cycle__section-icon" />
          <div>
            <h3 className="form-cycle__section-title">Test d'ovulation<InfoButton infoKey="formTestOvulation" /></h3>
            <p className="form-cycle__section-sub">Résultat du test effectué</p>
          </div>
        </div>
        <div className="form-cycle__options form-cycle__options--3col">
          {[
            { id: null, label: 'Non effectué', emoji: '—' },
            { id: false, label: 'Négatif', emoji: '❌' },
            { id: true, label: 'Positif', emoji: '✅' },
          ].map((o) => (
            <button
              key={String(o.id)}
              className={`form-cycle__option${testOvulation === o.id ? ' form-cycle__option--selected' : ''}`}
              onClick={() => { setTestOvulation(o.id); markDirty(); }}
            >
              <span style={{ fontSize: '1.4rem' }}>{o.emoji}</span>
              <span className="form-cycle__option-label">{o.label}</span>
            </button>
          ))}
        </div>

        {/* Déclaration directe, sous le test : le test dit « l'ovulation approche »,
            ce bouton dit « c'est aujourd'hui ». Les deux se complètent sans se
            confondre, d'où le séparateur et l'infobulle qui pose la condition d'usage. */}
        <button
          type="button"
          className={`form-cycle__declare${ovulationDeclared ? ' form-cycle__declare--on' : ''}`}
          aria-pressed={ovulationDeclared}
          onClick={() => { setOvulationDeclared(!ovulationDeclared); markDirty(); }}
        >
          <span className="form-cycle__declare-check" aria-hidden="true">
            {ovulationDeclared ? '✓' : ''}
          </span>
          <span className="form-cycle__declare-label">Ceci est mon jour d'ovulation</span>
        </button>
        <p className="form-cycle__declare-hint">
          À cocher seulement si tu en es sûre
          <InfoButton infoKey="formJourOvulation" />
        </p>
      </div>

      {/* ===== Symptômes physiques ===== */}
      <div className={`form-cycle__section form-cycle__section--col${jourRepos ? ' form-cycle__section--hidden' : ''}`}>
        <div className="form-cycle__section-header">
          <HeartbeatP className="form-cycle__section-icon" />
          <div>
            <h3 className="form-cycle__section-title">Symptômes physiques<InfoButton infoKey="formSymptomesPhysiques" /></h3>
            <p className="form-cycle__section-sub">Sélectionne ce que tu ressens</p>
          </div>
        </div>
        <div className="form-cycle__options form-cycle__options--3col">
          {[
            { id: 'fatigue', img: fatigue, label: 'Fatigue' },
            { id: 'ballonnements', img: ballonnements, label: 'Ballonnements' },
            { id: 'constipation', img: constipation, label: 'Constipation' },
            { id: 'jambes-lourdes', img: jambesLourdes, label: 'Jambes lourdes' },
            { id: 'maux-tete', img: mauxDeTete, label: 'Maux de tête' },
            { id: 'gonflement', img: gonflement, label: 'Gonflement poitrine' },
            { id: 'crampes', img: crampes, label: 'Crampes' },
            { id: 'acne', img: acne, label: 'Acné' },
            { id: 'courbatures', img: courbatures, label: 'Courbatures' },
            { id: 'selles-molles', emoji: '💩', label: 'Selles molles' },
            { id: 'douleurs-ovariennes', emoji: '🌸', label: 'Douleurs ovariennes' },
            { id: 'mal-dos', emoji: '🦴', label: 'Mal au dos' },
          ].map((o) => (
            <button
              key={o.id}
              className={`form-cycle__option${symptomes.includes(o.id) ? ' form-cycle__option--selected' : ''}`}
              onClick={() => toggleSymptome(o.id)}
            >
              {o.img
                ? <img src={o.img} alt={o.label} className="form-cycle__option-img" />
                : <span className="form-cycle__option-emoji">{o.emoji}</span>
              }
              <span className="form-cycle__option-label">{o.label}</span>
            </button>
          ))}
        </div>

        {/* Origine du mal de dos. Une douleur hormonale suit le cycle, une douleur
            mécanique suit la charge d'entraînement : les confondre noierait deux
            signaux distincts dans un seul compteur. */}
        {symptomes.includes('mal-dos') && (
          <div className="form-cycle__followup">
            <p className="form-cycle__section-sub">
              Ce mal de dos, plutôt&nbsp;?
              <InfoButton infoKey="formMalDeDos" />
            </p>
            <div className="form-cycle__craving-chips">
              {Object.entries(BACK_PAIN_MAP).map(([id, label]) => (
                <button
                  key={id}
                  className={`form-cycle__craving-chip${backPainType === id ? ' form-cycle__craving-chip--on' : ''}`}
                  aria-pressed={backPainType === id}
                  onClick={() => { setBackPainType(backPainType === id ? null : id); markDirty(); }}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ===== Qualité du sommeil ===== */}
      <div className={`form-cycle__section form-cycle__section--col${jourRepos ? ' form-cycle__section--hidden' : ''}`}>
        <div className="form-cycle__section-header">
          <Croissant className="form-cycle__section-icon" />
          <div>
            <h3 className="form-cycle__section-title">Qualité du sommeil</h3>
            <p className="form-cycle__section-sub">Comment as-tu dormi ?</p>
          </div>
        </div>
        <div className="form-cycle__options form-cycle__options--3col">
          {[
            { id: 'bien-dormi', img: habituelle, label: 'Bien dormi' },
            { id: 'reveils-freq', img: reveilsFreq, label: 'Réveils fréquents' },
            // Une coupure franche au milieu de la nuit, distincte de réveils
            // répétés : pas d'illustration dédiée, d'où l'emoji.
            { id: 'sommeil-interrompu', emoji: '🌙', label: 'Sommeil interrompu' },
            { id: 'diff-dormir', img: diffDormir, label: "Difficulté à s'endormir" },
          ].map((o) => (
            <button
              key={o.id}
              className={`form-cycle__option${sommeil === o.id ? ' form-cycle__option--selected' : ''}`}
              onClick={() => { setSommeil(sommeil === o.id ? null : o.id); markDirty(); }}
            >
              {o.img
                ? <img src={o.img} alt={o.label} className="form-cycle__option-img" />
                : <span className="form-cycle__option-emoji">{o.emoji}</span>
              }
              <span className="form-cycle__option-label">{o.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* ===== État au réveil ===== */}
      <div className={`form-cycle__section form-cycle__section--col${jourRepos ? ' form-cycle__section--hidden' : ''}`}>
        <div className="form-cycle__section-header">
          <Smiley className="form-cycle__section-icon" />
          <div>
            <h3 className="form-cycle__section-title">État au réveil<InfoButton infoKey="formEtatReveil" /></h3>
            <p className="form-cycle__section-sub">Comment te sens-tu ce matin ?</p>
          </div>
        </div>
        <div className="form-cycle__options form-cycle__options--3col">
          {[
            { id: 'fatiguee', img: fatigue, label: 'Fatiguée' },
            { id: 'normale', img: normale, label: 'Normale' },
            { id: 'pleine-energie', img: energie, label: "Pleine d'énergie" },
          ].map((o) => (
            <button
              key={o.id}
              className={`form-cycle__option${etatReveil === o.id ? ' form-cycle__option--selected' : ''}`}
              onClick={() => { setEtatReveil(etatReveil === o.id ? null : o.id); markDirty(); }}
            >
              <img src={o.img} alt={o.label} className="form-cycle__option-img" />
              <span className="form-cycle__option-label">{o.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* ===== Note personnelle ===== */}
      <div className={`form-cycle__section form-cycle__section--col form-cycle__section--note${jourRepos ? ' form-cycle__section--hidden' : ''}`}>
        <div className="form-cycle__section-header">
          <DropletP className="form-cycle__section-icon" />
          <h3 className="form-cycle__section-title">Note personnelle (optionnel)</h3>
        </div>
        <p className="form-cycle__note-sub">Notes sur ton cycle.</p>
        <textarea
          className="form-cycle__textarea form-cycle__textarea--note"
          placeholder="Ex. J'ai ressenti des crampes au réveil, légère fatigue en fin de journée..."
          value={note}
          onChange={(e) => { setNote(e.target.value); markDirty(); }}
          rows={3}
        />
      </div>

      {/* ===== Footer Enregistrer ===== */}
      <div className="form-cycle__footer">
        {error && <p style={{ color: 'var(--danger)', textAlign: 'center', marginBottom: '0.5rem', fontSize: '0.85rem' }}>{error}</p>}
        {(isDirty || existingId) && (
          <button className="form-cycle__submit" onClick={handleSubmit}>
            Enregistrer
          </button>
        )}
        <button className="form-cycle__cancel" onClick={() => navigate(-1)}>
          Annuler
        </button>
      </div>
    </div>
  );
};

export default FormCycle;
